import Vue from "vue";
import { Action, Module, Mutation, VuexModule } from "vuex-module-decorators";

import { DIContainer } from "@/app.container";
import { Dictionary } from "@/lib/Dictionary.type";
import dataStore from "@/store";

import FactoryRushCapacityMode from "../factory-rush-capacity-mode.model";
import FactoryRushCapacityProductGroupMember from "../factory-rush-capacity-product-group-member.model";
import FactoryRushCapacityProductGroup from "../factory-rush-capacity-product-group.model";
import FactoryRushCapacitySlot from "../factory-rush-capacity-slot.model";
import FactoryRushCapacityType from "../factory-rush-capacity-type.model";

const name = "FactoryRushCapacityStore";
const stateModules = (dataStore.state as unknown) as Record<string, unknown>;

if (stateModules[name]) {
  dataStore.unregisterModule(name);
}

const getDefaultState = () => {
  return {
    factoryRushCapacityMode: {},
    factoryRushCapacityProductGroup: {},
    factoryRushCapacityProductGroupMember: {},
    factoryRushCapacityType: {},
    factoryRushCapacitySlot: {},
    factorySlots: {},
    productGroupProducts: {},
  };
};

function createSlotKey(productGroupId: string, typeId: string): string {
  return `${productGroupId}:${typeId}`;
}

@Module({ name, dynamic: true, store: dataStore })
export default class FactoryRushCapacityStore extends VuexModule {
  factoryRushCapacityMode: Dictionary<boolean> = {};
  factoryRushCapacityProductGroup: Dictionary<FactoryRushCapacityProductGroup> = {};
  factoryRushCapacityProductGroupMember: Dictionary<FactoryRushCapacityProductGroupMember> = {};
  factoryRushCapacityType: Dictionary<FactoryRushCapacityType> = {};
  factoryRushCapacitySlot: Dictionary<FactoryRushCapacitySlot> = {};
  factorySlots: Dictionary<string[]> = {};
  productGroupProducts: Dictionary<string[]> = {};

  get allFactoryRushCapacityProductGroupsList(): FactoryRushCapacityProductGroup[] {
    return Object.values(
      this.factoryRushCapacityProductGroup
    ).sort((left, right) =>
      left.name.toLowerCase().localeCompare(right.name.toLowerCase())
    );
  }

  get factoryRushCapacityProductGroupsList(): FactoryRushCapacityProductGroup[] {
    return Object.values(this.factoryRushCapacityProductGroup)
      .filter(
        (productGroup) =>
          (this.productGroupProducts[productGroup.id] || []).length > 0
      )
      .sort((left, right) =>
        left.name.toLowerCase().localeCompare(right.name.toLowerCase())
      );
  }

  get hasFactoryRushCapacityProductGroupMembers(): (
    productGroupId: string
  ) => boolean {
    return (productGroupId: string) =>
      (this.productGroupProducts[productGroupId] || []).length > 0;
  }

  get factoryRushCapacityTypesList(): FactoryRushCapacityType[] {
    return Object.values(this.factoryRushCapacityType);
  }

  get getFactoryRushCapacityModeById(): (id: string) => boolean {
    return (id: string) => !!this.factoryRushCapacityMode[id];
  }

  get getFactoryRushCapacityProductGroupById(): (
    id: string
  ) => FactoryRushCapacityProductGroup | undefined {
    return (id: string) => this.factoryRushCapacityProductGroup[id];
  }

  get getFactoryRushCapacityProductGroupMemberByProductId(): (
    productId: string
  ) => FactoryRushCapacityProductGroupMember | undefined {
    return (productId: string) =>
      this.factoryRushCapacityProductGroupMember[productId];
  }

  get getFactoryRushCapacityProductIdsByProductGroupId(): (
    productGroupId: string
  ) => string[] {
    return (productGroupId: string) => [
      ...(this.productGroupProducts[productGroupId] || []),
    ];
  }

  get getFactoryRushCapacitySlotsByFactoryId(): (
    factoryId: string
  ) => FactoryRushCapacitySlot[] {
    return (factoryId: string) => {
      const ids = this.factorySlots[factoryId];

      if (!ids) {
        return [];
      }

      return ids.map((id) => this.factoryRushCapacitySlot[id]);
    };
  }

  get getFactoryRushCapacitySlotByFactoryProductGroupAndType(): (
    factoryId: string,
    productGroupId: string,
    typeId: string
  ) => FactoryRushCapacitySlot | undefined {
    return (factoryId: string, productGroupId: string, typeId: string) => {
      const ids = this.factorySlots[factoryId];

      if (!ids) {
        return undefined;
      }

      for (const id of ids) {
        const slot = this.factoryRushCapacitySlot[id];

        if (slot.productGroup === productGroupId && slot.type === typeId) {
          return slot;
        }
      }

      return undefined;
    };
  }

  @Mutation
  updateFactoryRushCapacityMode({
    id,
    value,
  }: {
    id: string;
    value: boolean;
  }): void {
    Vue.set(this.factoryRushCapacityMode, id, value);
  }

  @Mutation
  updateFactoryRushCapacityType(payload: FactoryRushCapacityType): void {
    Vue.set(this.factoryRushCapacityType, payload.id, payload);
  }

  @Mutation
  clearFactoryRushCapacityProductGroups(): void {
    this.factoryRushCapacityProductGroup = {};
  }

  @Mutation
  updateFactoryRushCapacityProductGroup(
    payload: FactoryRushCapacityProductGroup
  ): void {
    Vue.set(this.factoryRushCapacityProductGroup, payload.id, payload);
  }

  @Mutation
  removeFactoryRushCapacityProductGroup(id: string): void {
    Object.values(this.factoryRushCapacityProductGroupMember).forEach(
      (member) => {
        if (member.productGroup === id) {
          Vue.delete(
            this.factoryRushCapacityProductGroupMember,
            member.product
          );
        }
      }
    );

    Vue.delete(this.factoryRushCapacityProductGroup, id);
    Vue.delete(this.productGroupProducts, id);
  }

  @Mutation
  clearFactoryRushCapacityProductGroupMembers(): void {
    this.factoryRushCapacityProductGroupMember = {};
    this.productGroupProducts = {};
  }

  @Mutation
  updateFactoryRushCapacityProductGroupMember(
    payload: FactoryRushCapacityProductGroupMember
  ): void {
    Object.keys(this.productGroupProducts).forEach((productGroupId) => {
      const productIds = this.productGroupProducts[productGroupId];
      const memberIndex = productIds.indexOf(payload.product);

      if (memberIndex !== -1) {
        productIds.splice(memberIndex, 1);
      }
    });

    Vue.set(
      this.factoryRushCapacityProductGroupMember,
      payload.product,
      payload
    );

    if (!this.productGroupProducts[payload.productGroup]) {
      Vue.set(this.productGroupProducts, payload.productGroup, []);
    }

    this.productGroupProducts[payload.productGroup].push(payload.product);
  }

  @Mutation
  removeFactoryRushCapacityProductGroupMember(productId: string): void {
    const member = this.factoryRushCapacityProductGroupMember[productId];

    if (member) {
      const productIds = this.productGroupProducts[member.productGroup] || [];
      const memberIndex = productIds.indexOf(productId);

      if (memberIndex !== -1) {
        productIds.splice(memberIndex, 1);
      }
    }

    Vue.delete(this.factoryRushCapacityProductGroupMember, productId);
  }

  @Mutation
  clearFactoryRushCapacitySlots(): void {
    this.factoryRushCapacitySlot = {};
    this.factorySlots = {};
  }

  @Mutation
  updateFactoryRushCapacitySlots({
    factoryId,
    slots,
  }: {
    factoryId: string;
    slots: FactoryRushCapacitySlot[];
  }): void {
    const previousIds = this.factorySlots[factoryId] || [];
    const slotIds: string[] = [];
    const nextIds: Dictionary<boolean> = {};

    for (const slot of slots) {
      if (slot.factory !== factoryId) {
        throw new Error(
          "All rush capacity slots should belong to the specified factory"
        );
      }

      slotIds.push(slot.id);
      nextIds[slot.id] = true;
      Vue.set(this.factoryRushCapacitySlot, slot.id, slot);
    }

    for (const id of previousIds) {
      if (nextIds[id]) {
        continue;
      }

      Vue.delete(this.factoryRushCapacitySlot, id);
    }

    Vue.set(this.factorySlots, factoryId, slotIds);
  }

  @Action({ rawError: true })
  async enableFactoryRushCapacityModeById(id: string): Promise<void> {
    const mode = new FactoryRushCapacityMode(id);

    await DIContainer.FactoryRushCapacityModeRepository.save(mode);

    this.updateFactoryRushCapacityMode({ id, value: true });
  }

  @Action({ rawError: true })
  async disableFactoryRushCapacityModeById(id: string): Promise<void> {
    await DIContainer.FactoryRushCapacityModeRepository.deleteById(id);

    this.updateFactoryRushCapacityMode({ id, value: false });
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityModeById({
    id,
    useCache = true,
  }: {
    id: string;
    useCache?: boolean;
  }): Promise<boolean> {
    if (useCache && id in this.factoryRushCapacityMode) {
      return !!this.factoryRushCapacityMode[id];
    }

    const items = await DIContainer.FactoryRushCapacityModeRepository.getByIds([
      id,
    ]);

    const isEnabled = items[id] !== undefined;

    this.updateFactoryRushCapacityMode({ id, value: isEnabled });

    return isEnabled;
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityModesByIds({
    ids,
    useCache = true,
  }: {
    ids: string[];
    useCache?: boolean;
  }): Promise<Dictionary<boolean>> {
    const missing: string[] = [];
    const result: Dictionary<boolean> = {};

    ids.forEach((id) => {
      if (useCache && id in this.factoryRushCapacityMode) {
        result[id] = !!this.factoryRushCapacityMode[id];
        return;
      }

      missing.push(id);
    });

    if (!missing.length) {
      return result;
    }

    const items = await DIContainer.FactoryRushCapacityModeRepository.getByIds(
      missing
    );

    missing.forEach((id) => {
      const isEnabled = items[id] !== undefined;

      this.updateFactoryRushCapacityMode({ id, value: isEnabled });
      result[id] = isEnabled;
    });

    return { ...result };
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityTypes({
    useCache = true,
  }: {
    useCache?: boolean;
  } = {}): Promise<FactoryRushCapacityType[]> {
    if (!useCache || !this.factoryRushCapacityTypesList.length) {
      const collection = await DIContainer.FactoryRushCapacityTypeRepository.getList(
        1,
        999
      );

      collection.getItems().forEach((item) => {
        this.updateFactoryRushCapacityType(item);
      });
    }

    return this.factoryRushCapacityTypesList;
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityProductGroups({
    useCache = true,
  }: {
    useCache?: boolean;
  } = {}): Promise<FactoryRushCapacityProductGroup[]> {
    if (
      useCache &&
      Object.keys(this.factoryRushCapacityProductGroup).length > 0
    ) {
      return Object.values(this.factoryRushCapacityProductGroup);
    }

    const collection = await DIContainer.FactoryRushCapacityProductGroupRepository.getAll(
      useCache
    );
    const items = collection.getItems();

    this.clearFactoryRushCapacityProductGroups();

    items.forEach((item) => {
      this.updateFactoryRushCapacityProductGroup(item);
    });

    return items;
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacityProductGroupMembers({
    useCache = true,
  }: {
    useCache?: boolean;
  } = {}): Promise<FactoryRushCapacityProductGroupMember[]> {
    if (
      useCache &&
      Object.keys(this.factoryRushCapacityProductGroupMember).length > 0
    ) {
      return Object.values(this.factoryRushCapacityProductGroupMember);
    }

    const collection = await DIContainer.FactoryRushCapacityProductGroupMemberRepository.getAll(
      useCache
    );
    const items = collection.getItems();

    this.clearFactoryRushCapacityProductGroupMembers();

    items.forEach((item) => {
      this.updateFactoryRushCapacityProductGroupMember(item);
    });

    return items;
  }

  @Action({ rawError: true })
  async reloadFactoryRushCapacityProductGroupData(): Promise<void> {
    await Promise.all([
      this.loadFactoryRushCapacityProductGroups({ useCache: false }),
      this.loadFactoryRushCapacityProductGroupMembers({ useCache: false }),
    ]);
  }

  @Action({ rawError: true })
  async saveFactoryRushCapacityProductGroup(
    productGroup: FactoryRushCapacityProductGroup
  ): Promise<FactoryRushCapacityProductGroup> {
    const savedGroup = await DIContainer.FactoryRushCapacityProductGroupRepository.save(
      productGroup
    );

    this.updateFactoryRushCapacityProductGroup(savedGroup);

    return savedGroup;
  }

  @Action({ rawError: true })
  async deleteFactoryRushCapacityProductGroupById(id: string): Promise<void> {
    await DIContainer.FactoryRushCapacityProductGroupRepository.deleteById(id);
    this.removeFactoryRushCapacityProductGroup(id);
  }

  @Action({ rawError: true })
  async addFactoryRushCapacityProductGroupMember(
    member: FactoryRushCapacityProductGroupMember
  ): Promise<FactoryRushCapacityProductGroupMember> {
    const savedMember = await DIContainer.FactoryRushCapacityProductGroupMemberRepository.save(
      member
    );

    this.updateFactoryRushCapacityProductGroupMember(savedMember);

    return savedMember;
  }

  @Action({ rawError: true })
  async removeFactoryRushCapacityProductGroupMemberByProductId(
    productId: string
  ): Promise<void> {
    await DIContainer.FactoryRushCapacityProductGroupMemberRepository.deleteById(
      productId
    );
    this.removeFactoryRushCapacityProductGroupMember(productId);
  }

  @Action
  clearFactoryRushCapacitySlotCache(): void {
    this.clearFactoryRushCapacitySlots();
  }

  @Action({ rawError: true })
  async loadFactoryRushCapacitySlotsByFactoryId({
    factoryId,
    useCache = true,
  }: {
    factoryId: string;
    useCache?: boolean;
  }): Promise<FactoryRushCapacitySlot[]> {
    if (useCache && this.factorySlots[factoryId]) {
      return this.getFactoryRushCapacitySlotsByFactoryId(factoryId);
    }

    const collection = await DIContainer.FactoryRushCapacitySlotRepository.getByFactoryId(
      factoryId,
      useCache
    );

    const items = collection.getItems();

    this.updateFactoryRushCapacitySlots({ factoryId, slots: items });

    return items;
  }

  @Action({ rawError: true })
  async saveFactoryRushCapacitySlotsByFactoryId({
    factoryId,
    slots,
  }: {
    factoryId: string;
    slots: FactoryRushCapacitySlot[];
  }): Promise<FactoryRushCapacitySlot[]> {
    const currentSlots = await this.loadFactoryRushCapacitySlotsByFactoryId({
      factoryId,
    });

    const currentSlotsMap: Dictionary<FactoryRushCapacitySlot> = {};
    const desiredSlotsMap: Dictionary<FactoryRushCapacitySlot> = {};

    currentSlots.forEach((slot) => {
      currentSlotsMap[createSlotKey(slot.productGroup, slot.type)] = slot;
    });

    slots.forEach((slot) => {
      desiredSlotsMap[createSlotKey(slot.productGroup, slot.type)] = slot;
    });

    const savePromises: Promise<FactoryRushCapacitySlot>[] = [];
    const deletePromises: Promise<void>[] = [];

    Object.keys(desiredSlotsMap).forEach((key) => {
      const desiredSlot = desiredSlotsMap[key];
      const currentSlot = currentSlotsMap[key];

      if (!currentSlot) {
        desiredSlot.isNew = true;
        savePromises.push(
          DIContainer.FactoryRushCapacitySlotRepository.save(desiredSlot)
        );
        return;
      }

      if (currentSlot.capacity === desiredSlot.capacity) {
        return;
      }

      desiredSlot.isNew = false;
      savePromises.push(
        DIContainer.FactoryRushCapacitySlotRepository.save(desiredSlot)
      );
    });

    Object.keys(currentSlotsMap).forEach((key) => {
      if (desiredSlotsMap[key]) {
        return;
      }

      deletePromises.push(
        DIContainer.FactoryRushCapacitySlotRepository.delete(
          currentSlotsMap[key]
        )
      );
    });

    const requests: Array<Promise<unknown>> = [];

    savePromises.forEach((request) => {
      requests.push(request);
    });

    deletePromises.forEach((request) => {
      requests.push(request);
    });

    await Promise.all(requests);

    return this.loadFactoryRushCapacitySlotsByFactoryId({
      factoryId,
      useCache: false,
    });
  }

  @Mutation
  resetState(): void {
    const state = stateModules[name] as Record<string, unknown> | undefined;

    if (state) {
      Object.assign(state, getDefaultState());
    }
  }
}
